import React, { useEffect } from "react";
import { StyleSheet, View } from "react-native";
import {
  ActivityIndicator,
  List,
  Text,
  TouchableRipple,
  useTheme,
} from "react-native-paper";
import { useTranslation } from "react-i18next";
import {
  collection,
  db,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "../../../firebase";
import { USERS_COLLECTION } from "../../../constants/collections";

type TaskLogChange = {
  field?: string;
  before?: any;
  after?: any;
  type?: string;
};

type TaskLog = {
  id: string;
  addedBy?: string;
  changes?: TaskLogChange[];
  time?: string;
  timestamp?: { toDate?: () => Date };
};

type Props = {
  collectionName: string;
  taskId: string;
  subcollectionName: string;
  limitCount?: number;
};

const getLogDate = (log: TaskLog): Date | null => {
  if (log.timestamp && typeof log.timestamp.toDate === "function") {
    return log.timestamp.toDate();
  }

  if (typeof log.time === "string") {
    const d = new Date(log.time);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }

  return null;
};

const formatLogDate = (date: Date | null): string => {
  if (!date) {
    return "";
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function TaskActivityLogs({
  collectionName,
  taskId,
  subcollectionName,
  limitCount = 50,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [logs, setLogs] = React.useState<TaskLog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);
  const [expandedLogRows, setExpandedLogRows] = React.useState<
    Record<string, boolean>
  >({});
  const [userNamesById, setUserNamesById] = React.useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!expanded || hasLoadedOnce) {
      return;
    }

    let isCancelled = false;
    setLoading(true);
    const logsRef = collection(db, collectionName, taskId, subcollectionName);
    const logsQuery = query(
      logsRef,
      orderBy("timestamp", "desc"),
      limit(limitCount),
    );

    getDocs(logsQuery)
      .then(async (snapshot) => {
        if (isCancelled) {
          return;
        }

        const nextLogs = snapshot.docs.map((logDoc: any) => ({
          ...(logDoc.data() as Omit<TaskLog, "id">),
          id: logDoc.id,
        }));

        const missingUserIds = Array.from(
          new Set(
            nextLogs
              .map((log) => log.addedBy)
              .filter((addedBy): addedBy is string => !!addedBy),
          ),
        );

        if (missingUserIds.length > 0) {
          const users = await Promise.all(
            missingUserIds.map(async (userId) => {
              try {
                const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
                const fullName = userDoc.exists()
                  ? (userDoc.data()?.fullName as string | undefined)
                  : undefined;
                return { userId, fullName: fullName?.trim() || userId };
              } catch (error) {
                console.error(
                  "Failed to load user name for log:",
                  userId,
                  error,
                );
                return { userId, fullName: userId };
              }
            }),
          );

          if (!isCancelled) {
            setUserNamesById((prev) => {
              const next = { ...prev };
              users.forEach(({ userId, fullName }) => {
                next[userId] = fullName;
              });
              return next;
            });
          }
        }

        if (!isCancelled) {
          setLogs(nextLogs);
          setHasLoadedOnce(true);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          console.error("Failed to load task logs:", error);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    collectionName,
    expanded,
    hasLoadedOnce,
    limitCount,
    subcollectionName,
    taskId,
  ]);

  useEffect(() => {
    setLogs([]);
    setLoading(false);
    setHasLoadedOnce(false);
    setExpandedLogRows({});
    setUserNamesById({});
  }, [collectionName, taskId, subcollectionName]);

  const formatChangeValue = React.useCallback(
    (fieldName: string | undefined, value: any) => {
      if (value === null || typeof value === "undefined") {
        return "—";
      }

      if (typeof value === "string") {
        const looksLikeIsoDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(
          value,
        );
        if (looksLikeIsoDate) {
          const parsed = new Date(value);
          if (!Number.isNaN(parsed.getTime())) {
            return formatLogDate(parsed);
          }
        }

        if (fieldName === "status") {
          return t(value, { defaultValue: value });
        }

        return value;
      }

      if (typeof value === "number") {
        return String(value);
      }

      if (typeof value === "boolean") {
        return value
          ? t("Yes", { defaultValue: "Yes" })
          : t("No", { defaultValue: "No" });
      }

      if (value && typeof value.toDate === "function") {
        const parsed = value.toDate();
        return formatLogDate(parsed);
      }

      if (Array.isArray(value)) {
        if (value.length === 0) {
          return "[]";
        }

        const allPrimitive = value.every(
          (item) =>
            item === null ||
            typeof item === "string" ||
            typeof item === "number" ||
            typeof item === "boolean",
        );

        if (allPrimitive) {
          const shown = value
            .slice(0, 3)
            .map((item) => String(item))
            .join(", ");
          return value.length > 3 ? `${shown}…` : shown;
        }

        return `${value.length} ${t("items", { defaultValue: "items" })}`;
      }

      if (typeof value === "object") {
        if (typeof value.fullName === "string" && value.fullName.trim()) {
          return value.fullName.trim();
        }
        if (typeof value.name === "string" && value.name.trim()) {
          return value.name.trim();
        }
        if (typeof value.title === "string" && value.title.trim()) {
          return value.title.trim();
        }
        if (typeof value.id === "string" && value.id.trim()) {
          return value.id.trim();
        }
        return t("updated value", { defaultValue: "updated value" });
      }

      return String(value);
    },
    [t],
  );

  const getChangeLabel = React.useCallback(
    (fieldName?: string) => {
      if (!fieldName) {
        return t("Field", { defaultValue: "Field" });
      }
      return t(fieldName, { defaultValue: fieldName });
    },
    [t],
  );

  const getLogMessage = React.useCallback(
    (log: TaskLog) => {
      const userName = log.addedBy
        ? userNamesById[log.addedBy] || log.addedBy
        : t("Unknown", { defaultValue: "Unknown" });
      const statusChange = log.changes?.find(
        (change) => change.field === "status" && change.type === "updated",
      );

      if (statusChange) {
        const beforeRaw = String(statusChange.before ?? "");
        const afterRaw = String(statusChange.after ?? "");
        const fromLabel = t(beforeRaw, { defaultValue: beforeRaw });
        const toLabel = t(afterRaw, { defaultValue: afterRaw });

        return t("cardLog.statusMoved", {
          user: userName,
          from: fromLabel,
          to: toLabel,
          defaultValue: `${userName} moved this card: from "${fromLabel}" to "${toLabel}"`,
        });
      }

      return t("cardLog.updated", {
        user: userName,
        defaultValue: `${userName} updated this card`,
      });
    },
    [t, userNamesById],
  );

  return (
    <View style={styles.container}>
      <List.Accordion
        title={t("Activity")}
        expanded={expanded}
        onPress={() => setExpanded((prev) => !prev)}
        style={styles.accordion}
      >
        {loading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="small" />
          </View>
        ) : logs.length === 0 ? (
          <Text
            style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}
          >
            {t("No activity yet")}
          </Text>
        ) : (
          logs.map((log) => (
            <TouchableRipple
              key={log.id}
              disabled={!log.changes?.length}
              onPress={() =>
                setExpandedLogRows((prev) => ({
                  ...prev,
                  [log.id]: !prev[log.id],
                }))
              }
              style={styles.logItem}
              borderless
            >
              <View>
                <Text style={styles.message}>{getLogMessage(log)}</Text>
                <Text
                  style={[
                    styles.date,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatLogDate(getLogDate(log))}
                </Text>
                {!!log.changes?.length && expandedLogRows[log.id] && (
                  <View style={styles.changeList}>
                    {log.changes.map((change, index) => {
                      const label = getChangeLabel(change.field);
                      const before = formatChangeValue(
                        change.field,
                        change.before,
                      );
                      const after = formatChangeValue(
                        change.field,
                        change.after,
                      );

                      return (
                        <Text
                          key={`${log.id}-change-${index}`}
                          style={[
                            styles.changeLine,
                            { color: theme.colors.onSurfaceVariant },
                          ]}
                        >
                          {`${label}: "${before}" -> "${after}"`}
                        </Text>
                      );
                    })}
                  </View>
                )}
              </View>
            </TouchableRipple>
          ))
        )}
      </List.Accordion>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    gap: 8,
  },
  accordion: {
    paddingHorizontal: 0,
    backgroundColor: "transparent",
  },
  loading: {
    paddingVertical: 8,
    alignItems: "flex-start",
  },
  emptyText: {
    fontSize: 13,
  },
  logItem: {
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(127,127,127,0.35)",
  },
  message: {
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
  },
  date: {
    marginTop: 4,
    fontSize: 13,
    textDecorationLine: "underline",
  },
  changeList: {
    marginTop: 8,
    gap: 4,
  },
  changeLine: {
    fontSize: 13,
    lineHeight: 18,
  },
});
